import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import { Layout } from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Clock, Syringe, User as UserIcon, Building2, CreditCard, Lock, CheckCircle2,
  Edit2, Loader2, Radio, Search, Trash2, FileText, RefreshCw, Megaphone, ChevronDown, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const relTime = (iso) => {
  if (!iso) return '—';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'hace unos segundos';
    if (diff < 3600) return `hace ${Math.floor(diff/60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff/3600)} h`;
    return new Date(iso).toLocaleString('es-CL');
  } catch { return iso; }
};

const VacunacionesPendientesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [borradores, setBorradores] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // opened borrador for confirmation
  const [confirming, setConfirming] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // for success animation
  const [deletingId, setDeletingId] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [callingId, setCallingId] = useState(null);
  const [lastCalled, setLastCalled] = useState(null);
  const [boletaEdits, setBoletaEdits] = useState({});
  const [savingBoletaId, setSavingBoletaId] = useState(null);

  const handleSaveBoleta = async (b) => {
    const valor = boletaEdits[b.id];
    if (valor === undefined || valor.trim() === (b.numero_boleta || '')) return;
    setSavingBoletaId(b.id);
    try {
      await axios.put(`${API}/vaccinations/borradores/${b.id}/boleta`,
        { numero_boleta: valor.trim() },
        { withCredentials: true }
      );
      toast.success('Nº de boleta guardado');
      setBoletaEdits(prev => {
        const copy = { ...prev };
        delete copy[b.id];
        return copy;
      });
      fetchBorradores();
    } catch (error) {
      console.error('Error saving boleta:', error);
      toast.error('Error al guardar el Nº de boleta');
    } finally {
      setSavingBoletaId(null);
    }
  }; // {numero, box_nombre, nombre_completo}
  const wsRef = useRef(null);

  // Nurse-editable fields (Step 2)
  const [nurseFields, setNurseFields] = useState({
    lote_serie: '',
    administracion: '',
    comentarios: '',
    registro_rni: '',
  });

  const fetchBorradores = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vaccinations/borradores?only_pending=true`, { withCredentials: true });
      setBorradores(res.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar los borradores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBorradores();
    (async () => {
      try {
        const r = await axios.get(`${API}/puntos-atencion?activos_only=true`, { withCredentials: true });
        setBoxes(r.data || []);
      } catch (e) {
        console.warn('No boxes loaded:', e);
      }
    })();
  }, [fetchBorradores]);

  const handleLlamarBox = async (b, box_numero) => {
    setCallingId(b.id);
    try {
      const res = await axios.post(
        `${API}/vaccinations/borradores/${b.id}/llamar-box`,
        { box_numero: box_numero ?? null },
        { withCredentials: true }
      );
      const info = res.data;
      setLastCalled({
        numero: info.numero_llamado,
        box_nombre: info.box?.nombre,
        box_color: info.box?.color,
        nombre_completo: b.nombre_completo,
      });
      toast.success(`📣 ${info.numero_llamado} llamado a ${info.box?.nombre}`);
      setTimeout(() => setLastCalled(null), 6000);
      fetchBorradores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al llamar');
    } finally {
      setCallingId(null);
    }
  };

  // WebSocket for real-time updates
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) return;
    const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws/vacunaciones';
    let reconnectTimeout = null;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => setWsConnected(true);
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.type === 'BORRADOR_CREADO' || msg.type === 'BORRADOR_ACTUALIZADO' || msg.type === 'BORRADOR_CONFIRMADO') {
              fetchBorradores();
            }
          } catch (e) {
            console.warn('WS parse error:', e);
          }
        };
        ws.onclose = () => {
          setWsConnected(false);
          wsRef.current = null;
          reconnectTimeout = setTimeout(connect, 3000);
        };
        ws.onerror = () => {};
      } catch (e) {
        console.warn('WS connect failed:', e);
        reconnectTimeout = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (wsRef.current) wsRef.current.close();
    };
  }, [fetchBorradores]);

  const openBorrador = (b) => {
    setSelected(b);
    setNurseFields({
      lote_serie: b.lote_serie || '',
      administracion: b.administracion || '',
      comentarios: b.comentarios || '',
      registro_rni: b.registro_rni || '',
    });
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setConfirming(true);
    try {
      const res = await axios.post(
        `${API}/vaccinations/borradores/${selected.id}/confirmar`,
        nurseFields,
        { withCredentials: true }
      );
      setSelected(null);
      setConfirmed(res.data);
      toast.success(`✅ ${res.data.nombre_completo} vacunado correctamente`);
      // Auto-dismiss success card after 4s
      setTimeout(() => setConfirmed(null), 4000);
      fetchBorradores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al confirmar vacunación');
    } finally {
      setConfirming(false);
    }
  };

  const handleDelete = async (b) => {
    setDeletingId(b.id);
    try {
      await axios.delete(`${API}/vaccinations/borradores/${b.id}`, { withCredentials: true });
      toast.success('Borrador eliminado');
      setShowDelete(null);
      fetchBorradores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = borradores.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (b.nombre_completo || '').toLowerCase().includes(q) ||
      (b.rut || '').toLowerCase().includes(q) ||
      (b.vacuna || '').toLowerCase().includes(q) ||
      (b.empresa || '').toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6 animate-fade-in" data-testid="vacunaciones-pendientes-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Syringe className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 heading">Vacunaciones Pendientes</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Pacientes registrados por recepción, esperando confirmación de vacunación
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${wsConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
              <Radio className={`w-3 h-3 ${wsConnected ? 'animate-pulse' : ''}`} />
              {wsConnected ? 'Tiempo real activo' : 'Sin conexión'}
            </div>
            <Button variant="outline" onClick={fetchBorradores} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Last called banner */}
        {lastCalled && (
          <Card className="border-2 animate-fade-in" style={{ borderColor: lastCalled.box_color || '#3B82F6' }}>
            <CardContent className="py-4 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: lastCalled.box_color || '#3B82F6' }}>
                <Megaphone className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-bold">Llamado en curso</div>
                <div className="text-lg font-bold text-slate-900 mt-0.5">
                  <span className="font-mono">{lastCalled.numero}</span> — {lastCalled.nombre_completo}
                </div>
                <div className="text-sm text-slate-600">Pase al <strong>{lastCalled.box_nombre}</strong></div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setLastCalled(null)}>Cerrar</Button>
            </CardContent>
          </Card>
        )}

        {/* Success animation card */}
        {confirmed && (
          <Card className="border-2 border-emerald-500 bg-emerald-50 animate-fade-in">
            <CardContent className="py-6 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 animate-pulse">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-bold text-emerald-900">✅ VACUNADO</div>
                <div className="text-emerald-800 mt-0.5">
                  <strong>{confirmed.nombre_completo}</strong> — {confirmed.vacuna}
                </div>
                <div className="text-xs text-emerald-700 mt-1">Registrado correctamente</div>
              </div>
              <Button variant="ghost" onClick={() => setConfirmed(null)}>Cerrar</Button>
            </CardContent>
          </Card>
        )}

        {/* Search bar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre, RUT, vacuna o empresa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-borradores-input"
            />
          </div>
          <Badge variant="outline" className="text-sm">
            {filtered.length} pendiente{filtered.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center">
            <Syringe className="w-14 h-14 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 font-medium">No hay pacientes pendientes</p>
            <p className="text-sm text-slate-400 mt-1">
              Cuando la recepcionista envíe pacientes, aparecerán aquí en tiempo real.
            </p>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filtered.map((b) => (
              <Card
                key={b.id}
                className="border-l-4 border-l-blue-500 hover:shadow-md hover:border-l-blue-600 transition-all cursor-pointer"
                onClick={() => openBorrador(b)}
                data-testid={`borrador-${b.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-3">
                        <UserIcon className="w-5 h-5 text-slate-400" />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900" data-testid={`borrador-name-${b.id}`}>
                              {b.nombre_completo}
                            </h3>
                            {b.pago_familiar && (
                              <Badge className="bg-purple-100 text-purple-700 border border-purple-200 gap-1 text-[10px] py-0" data-testid={`badge-familiar-${b.id}`}>
                                <Users className="w-3 h-3" />
                                Familiar
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                            <span className="font-mono">{b.rut || b.pasaporte || b.otro_documento}</span>
                            {b.edad && <span>{b.edad} años</span>}
                            {b.comuna && <span>{b.comuna}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <Syringe className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-slate-900">{b.vacuna}</span>
                      </div>
                      {b.empresa && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Building2 className="w-3 h-3" />
                          {b.empresa}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex flex-col items-end gap-1 min-w-[140px]">
                      {b.numero_llamado ? (
                        <Badge className="gap-1 text-white border-0" style={{ background: '#3B82F6' }}>
                          <Megaphone className="w-3 h-3" />
                          {b.numero_llamado} → {b.box_nombre}
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 gap-1">
                          <Clock className="w-3 h-3" />
                          {relTime(b.fecha_borrador)}
                        </Badge>
                      )}
                      <div className="text-[11px] text-slate-500">
                        por <strong>{b.created_by_name || '—'}</strong>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <CreditCard className="w-4 h-4 text-slate-400" />
                        <Input
                          value={boletaEdits[b.id] !== undefined ? boletaEdits[b.id] : (b.numero_boleta || '')}
                          onChange={(e) => setBoletaEdits(prev => ({ ...prev, [b.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveBoleta(b)}
                          placeholder="Nº Boleta"
                          className="h-8 w-28 text-sm"
                          data-testid={`input-boleta-${b.id}`}
                        />
                        {boletaEdits[b.id] !== undefined && boletaEdits[b.id].trim() !== (b.numero_boleta || '') && (
                          <Button
                            size="sm"
                            className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700"
                            disabled={savingBoletaId === b.id}
                            onClick={() => handleSaveBoleta(b)}
                            title="Guardar Nº de boleta"
                            data-testid={`save-boleta-${b.id}`}
                          >
                            {savingBoletaId === b.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            className="gap-1 bg-blue-600 hover:bg-blue-700"
                            disabled={callingId === b.id}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`llamar-box-btn-${b.id}`}
                          >
                            {callingId === b.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Megaphone className="w-4 h-4" />
                            )}
                            {b.numero_llamado ? `Rellamar ${b.numero_llamado}` : 'Llamar a box'}
                            <ChevronDown className="w-3 h-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuLabel>Elegir destino</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleLlamarBox(b, null); }}>
                            <Megaphone className="w-4 h-4 mr-2 text-blue-600" />
                            Auto (recomendado)
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {boxes.length === 0 && (
                            <DropdownMenuItem disabled>No hay boxes activos</DropdownMenuItem>
                          )}
                          {boxes.map((box) => (
                            <DropdownMenuItem
                              key={box.id}
                              onClick={(e) => { e.stopPropagation(); handleLlamarBox(b, box.numero); }}
                              data-testid={`llamar-a-box-${box.numero}-${b.id}`}
                            >
                              <span
                                className="w-2.5 h-2.5 rounded-full mr-2"
                                style={{ background: box.color || '#3B82F6' }}
                              />
                              {box.nombre}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); navigate(`/vacunacion/editar/${b.id}`); }}
                        title="Modificar"
                        className="gap-1"
                        data-testid={`edit-borrador-${b.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                        Modificar
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                        onClick={(e) => { e.stopPropagation(); openBorrador(b); }}
                        data-testid={`open-borrador-${b.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Vacunar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); setShowDelete(b); }}
                        title="Descartar borrador"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Confirm Vaccination Dialog */}
        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                Confirmar vacunación
              </DialogTitle>
              <DialogDescription>
                Revisa los datos registrados por recepción y completa la información clínica.
              </DialogDescription>
            </DialogHeader>

            {selected && (
              <div className="space-y-5 py-3">
                {/* Patient info (read-only) */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Paciente</div>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Nombre</div>
                      <div className="font-medium text-slate-900">{selected.nombre_completo}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Documento</div>
                      <div className="font-mono text-slate-900">
                        {selected.rut || selected.pasaporte || selected.otro_documento || '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Fecha nacimiento</div>
                      <div className="text-slate-900">{selected.fecha_nacimiento || '—'} {selected.edad && `(${selected.edad} años)`}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Comuna</div>
                      <div className="text-slate-900">{selected.comuna || '—'}</div>
                    </div>
                  </div>
                </div>

                {/* Vaccine info (read-only) */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold mb-2">Vacuna a aplicar</div>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Vacuna(s)</div>
                      <div className="font-medium text-slate-900">{selected.vacuna}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Tipo</div>
                      <div className="text-slate-900">{selected.tipo_vacunacion || '—'}</div>
                    </div>
                    {selected.empresa && (
                      <div className="sm:col-span-2">
                        <div className="text-xs text-slate-500">Empresa / Convenio</div>
                        <div className="text-slate-900 font-medium">{selected.empresa}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment info (read-only, locked) */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Datos de pago</div>
                    <Badge variant="outline" className="gap-1 text-[10px] py-0 px-2">
                      <Lock className="w-2.5 h-2.5" />
                      solo lectura
                    </Badge>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Monto</div>
                      <div className="font-mono text-slate-900 font-semibold">
                        ${(selected.monto || 0).toLocaleString('es-CL')}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Forma de pago</div>
                      <div className="text-slate-900">{selected.tipo_pago || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Boleta</div>
                      <div className="font-mono text-slate-900">{selected.numero_boleta || '—'}</div>
                    </div>
                  </div>
                </div>

                {/* Nurse-editable clinical fields */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-3">
                    Datos clínicos (completa aquí)
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="lote_serie" className="text-xs">Lote / Serie</Label>
                      <Input
                        id="lote_serie"
                        value={nurseFields.lote_serie}
                        onChange={(e) => setNurseFields({ ...nurseFields, lote_serie: e.target.value })}
                        placeholder="Ej: A1B2C3"
                        className="mt-1 font-mono"
                        data-testid="lote-serie-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="administracion" className="text-xs">Sitio de aplicación</Label>
                      <Input
                        id="administracion"
                        value={nurseFields.administracion}
                        onChange={(e) => setNurseFields({ ...nurseFields, administracion: e.target.value })}
                        placeholder="Ej: Deltoides izquierdo"
                        className="mt-1"
                        data-testid="administracion-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="registro_rni" className="text-xs">Registro RNI (opcional)</Label>
                      <Input
                        id="registro_rni"
                        value={nurseFields.registro_rni}
                        onChange={(e) => setNurseFields({ ...nurseFields, registro_rni: e.target.value })}
                        placeholder="Nº de registro en Sistema RNI"
                        className="mt-1"
                        data-testid="registro-rni-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="comentarios" className="text-xs">Observaciones</Label>
                      <Textarea
                        id="comentarios"
                        value={nurseFields.comentarios}
                        onChange={(e) => setNurseFields({ ...nurseFields, comentarios: e.target.value })}
                        placeholder="Reacción, notas adicionales..."
                        rows={2}
                        className="mt-1"
                        data-testid="comentarios-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Audit trail hint if there's history */}
                {selected.change_log && selected.change_log.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <FileText className="w-3 h-3" />
                    Este registro tiene <strong>{selected.change_log.length}</strong> cambio(s) auditados.
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setSelected(null)} disabled={confirming}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={confirming}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid="confirm-vacunacion-btn"
              >
                {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Confirmar vacunación
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <AlertDialog open={!!showDelete} onOpenChange={(o) => !o && setShowDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar borrador?</AlertDialogTitle>
              <AlertDialogDescription>
                Se descartará el borrador de <strong>{showDelete?.nombre_completo}</strong>. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => showDelete && handleDelete(showDelete)}
                disabled={deletingId === showDelete?.id}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingId === showDelete?.id ? 'Eliminando...' : 'Eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default VacunacionesPendientesPage;
